import {
  getRegionTreeApi,
  getDictApi_p,
  getTableColumnsApi,
  getEnumApi,
  previewFileApi,
  getFileListApi
} from '@/api/publicAxios/publicAxios'
import { useRouter } from 'vue-router'
import { fetchAndProcessTableColumns } from '@/utils'
import { pinyin } from 'pinyin-pro'
import { $message } from './message'
import { useStorage } from '@/hooks/web/useStorage'

export const publicData = {
  // 默认搜索城市名称（天地图地点搜索行政区）
  cityName: '成都'
}

const { getStorage } = useStorage()

// 获取城市数据
export const p_GetCityFun = async (filterType?: string) => {
  try {
    const res = await getRegionTreeApi()
    if (res.code === 0) {
      function childCity(data) {
        return data.map((item) => ({
          ...item,
          label: item.name,
          value: item.id,
          children: item.children ? childCity(item.children) : []
        }))
      }

      let processedData = childCity(res.data)

      // 如果传入参数'city'，则只保留第一层children数据，返回扁平数组
      if (filterType === 'city') {
        // 收集所有第一层的children数据
        const cityData: any[] = []

        processedData.forEach((province) => {
          if (province.children && province.children.length > 0) {
            // 将每个省份的子级（市）添加到结果数组中
            province.children.forEach((city) => {
              cityData.push({
                longitude: city.longitude,
                latitude: city.latitude,
                label: city.name,
                value: city.id
                // 清除子级数据，确保只保留市级别
                // children: []
              })
            })
          }
        })

        processedData = cityData
      } else if (filterType === 'pro') {
        processedData = processedData.map((province) => ({
          longitude: province.longitude,
          latitude: province.latitude,
          label: province.name,
          value: province.id
          // 清除子级数据
          // children: []
        }))
      } else if (filterType === 'proCity') {
        processedData = processedData.map((province) => ({
          longitude: province.longitude,
          latitude: province.latitude,
          label: province.name,
          value: province.id,
          children: province.children.map((city) => ({
            longitude: city.longitude,
            latitude: city.latitude,
            label: city.name,
            value: city.id,
            parentId: city.parentId
            // 清除子级数据
            // children: []
          }))
        }))
      }
      return processedData
    }
    return []
  } catch (error) {
    console.error('获取城市数据失败:', error)
    return []
  }
}

// 获取字典数据（下拉 options：label=name, value=code）
export const p_GetDictFun = async (dictType: string) => {
  try {
    // 假设有一个获取字典数据的API函数 getDictApi ，根据dictType获取字典数据
    const res = await getDictApi_p(dictType)
    if (res.code === 0) {
      return (res.data.list || []).map((item) => ({
        ...item,
        label: item.name,
        value: item.code
      }))
    }
    return []
  } catch (error) {
    console.error(`获取字典数据失败: ${dictType}`, error)
    return []
  }
}

/** 字典 options → { code: name }，用于列表展示文案 */
export const p_DictToLabelMap = (
  options: Array<{ label?: string; value?: string | number; name?: string; code?: string }>
): Record<string, string> => {
  const map: Record<string, string> = {}
  ;(options || []).forEach((item) => {
    const key = String(item.value ?? item.code ?? '')
    if (!key) return
    map[key] = String(item.label ?? item.name ?? key)
  })
  return map
}

/** 在字典 options 前插入「全部」筛选项 */
export const p_WithAllOption = (
  options: Array<{ label: string; value: any }>,
  allLabel = '全部',
  allValue: any = ''
) => [{ label: allLabel, value: allValue }, ...(options || [])]

// 获取自定义列表（可传 menuCode 覆盖当前路由，供网约车/出租车订单等同页多菜单场景）
export const p_GetTableColumns = async (
  convertFixed: boolean | string = false,
  menuCodeOverride?: string
) => {
  // 兼容旧调用：p_GetTableColumns(true) / p_GetTableColumns('xxxMenuCode')
  let convert = false
  let override = menuCodeOverride
  if (typeof convertFixed === 'string') {
    override = convertFixed
    convert = false
  } else {
    convert = !!convertFixed
  }
  const empty = { tableAllColumns: [] as any[], showColumns: [] as string[] }
  const currentRoute = useRouter()?.currentRoute?.value
  const menuId = (currentRoute?.meta?.id as string) || ''
  // 登录菜单里 name 即为 base_menu.code，用于后端按编码解析真实 menuId（与库中 base_custom_columns.menu_id 对齐）
  const menuCode =
    override ||
    (currentRoute?.name as string) ||
    (currentRoute?.meta?.activeMenu as string) ||
    (typeof currentRoute?.path === 'string'
      ? currentRoute.path.split('/').filter(Boolean).slice(-1)[0]
      : '')
  if (!menuId && !menuCode) {
    return empty
  }
  const res = await getTableColumnsApi({
    // 有明确 menuCode 时优先只传 code，避免缓存旧 meta.id 指向错误菜单
    ...(menuCode ? { menuCode } : menuId ? { menuId } : {})
  })
  if (res.code !== 0) {
    $message.error(res.message || '获取自定义列表失败')
    return empty
  }
  if (!Array.isArray(res.data)) {
    return empty
  }
  const tableAllColumns = res.data.map((item: any) => {
    const column: any = {
      ...item,
      label: item.columnsName,
      field: item.columnsKey
    }
    // 仅在有有效列宽时写入；空值勿设 width:null/0，否则易触发固定列中间留白
    const len = Number(item.columnsLength)
    if (!Number.isNaN(len) && len > 0) {
      column.minWidth = len
    }

    // 如果需要转换fixed参数，则进行转换
    if (convert) {
      switch (item.fixed) {
        case 1:
          column.fixed = 'left'
          break
        case 2:
          column.fixed = 'right'
          break
        case 0:
        default:
          column.fixed = false
          break
      }
    }
    return column
  })
  // columnsShow：兼容 number/string/boolean；null/undefined 按「显示」兜底，避免映射异常导致整表只剩操作列
  const isColumnVisible = (v: unknown) => {
    if (v === false || v === 0 || v === '0') return false
    if (v === true || v === 1 || v === '1') return true
    return v == null || v === ''
  }
  const showColumns = tableAllColumns
    .filter((item) => isColumnVisible(item.columnsShow))
    .map((child) => child.field)
    .filter((field): field is string => !!field)
  // 若全部被误判为隐藏，仍回退展示全部有 field 的列，避免列表空白
  const visibleFields =
    showColumns.length > 0
      ? showColumns
      : tableAllColumns.map((c) => c.field).filter((field): field is string => !!field)
  return {
    tableAllColumns,
    showColumns: visibleFields
  }
}

// 生成随机32位uuid
export const p_GenerateRandom32 = () => {
  let result = ''
  for (let i = 0; i < 4; i++) {
    result += Math.random().toString(36).substring(2, 10)
  }
  return result
}

/**
 * 获取中文文本的拼音首字母
 * @param chineseText 中文文本
 * @returns 拼音首字母（大写）
 */
export const p_GetFirstLetter = (chineseText: string): string => {
  if (!chineseText) return ''

  try {
    // 只取第一个字符
    const firstChar = chineseText.charAt(0)
    const result = pinyin(firstChar, {
      pattern: 'firstLetter',
      toneType: 'none'
    })
    return result ? result.toString().toUpperCase().charAt(0) : ''
  } catch (error) {
    console.error('拼音转换失败:', error)
    return ''
  }
}

/**
 * 根据 schema 过滤表单数据，只保留 schema 中定义的字段
 * @param formData 原始表单数据
 * @param schema 表单 schema 配置
 * @returns 过滤后的数据对象
 */
export const p_FilterFormDataBySchema = (formData: Record<string, any>, schema: any[]) => {
  // 提取 schema 中所有有效的 field 值
  const validFields = schema.map((item) => item.field).filter((field) => field && field !== '') // 过滤掉空字段和无效字段

  // 创建新的对象，只保留有效字段
  const filteredData: Record<string, any> = {}

  validFields.forEach((field) => {
    if (field in formData) {
      filteredData[field] = formData[field]
    }
  })

  return filteredData
}

/**
 * 枚举数据转为下拉选项数据
 * @param enumData 枚举标识符，可以是单个字符串或逗号分隔的多个字符串
 * @returns 返回对象，键名为枚举标识符，键值为对应的选项数组
 */
export const p_EnumToSelectOptions = async (enumData: string) => {
  // 处理输入参数，转换为字符串数组
  const enumKeys = enumData
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item)

  if (enumKeys.length === 0) {
    return {}
  }

  try {
    // 调用接口获取枚举数据
    const result = await getEnumApi(enumData)

    if (!result || typeof result !== 'object') {
      console.error('获取枚举数据失败: 响应格式异常', result)
      return {}
    }

    if (result.code === 0) {
      const enumObj = result.data

      // 构建返回对象，键名为枚举标识符，键值为对应的选项数组
      const resultMap: Record<string, any[]> = {}

      enumKeys.forEach((key) => {
        // 根据实际数据结构获取对应的枚举数据
        const enumDataItem = enumObj[key]?.data || {}
        // 将对象格式转换为选项数组
        resultMap[key] = Object.keys(enumDataItem).map((itemKey) => {
          // 检查值是否为纯数字，如果是则转换为number类型
          const value = /^\d+$/.test(itemKey) ? Number(itemKey) : itemKey
          return {
            label: enumDataItem[itemKey] || '',
            value: value
          }
        })
      })

      return resultMap
    }

    console.error('获取枚举数据失败:', {
      enumData,
      code: result.code,
      message: result.message,
      raw: result
    })
    return {}
  } catch (error) {
    console.error('获取枚举数据异常:', error)
    return {}
  }
}

/**
 * 根据枚举数据直接返回格式化数组选项
 * @param enumType 枚举类型标识符
 * @returns 返回格式化后的选项数组，label使用键值，value使用键名
 */
export const p_GetEnumOptions = async (enumType: string) => {
  try {
    // 调用接口获取枚举数据
    const result = await getEnumApi(enumType)

    if (result.code === 0) {
      const enumData = result.data[enumType] || []
      console.log('enumData', enumData)

      // 转换为选项数组，label使用键值，value使用键名
      // return enumData.data.map((item: any) => ({
      //   label: item.value || item.label || '',
      //   value: item.key || item.code || ''
      // }))
      return Object.keys(enumData.data).map((key) => ({
        label: enumData.data[key],
        value: key
      }))
    }
    return []
  } catch (error) {
    console.error(`获取枚举数据异常: ${enumType}`, error)
    return []
  }
}

/**
 * 预览文件-返回可查看的图片信息
 * @param fileId 文件id
 * @returns 文件url
 */

export const p_PreviewFile = async (fileId: string) => {
  if (!fileId) return undefined
  try {
    const res: any = await previewFileApi(fileId, { silentError: true })
    if (!res || res._isError || res._isLoginExpired) return undefined
    const blobData = res.data ?? res
    if (!(blobData instanceof Blob)) return undefined
    // 后端异常时常以 JSON + blob 返回，勿当成图片
    const type = (blobData.type || '').toLowerCase()
    if (type && !type.startsWith('image/') && type !== 'application/octet-stream') {
      return undefined
    }
    if (type.includes('json') || type.includes('text')) {
      return undefined
    }
    const imageUrl = window.URL.createObjectURL(blobData)
    return imageUrl
  } catch (error) {
    console.log('error', error)
    return undefined
  }
}

/**
 * 公共导出方法
 * @ id: 菜单id
 * @ data: 导出搜索参数无分页
 */
export const p_Export = async (requestFun, id, data, onFinish) => {
  try {
    const res = await requestFun(id, data, { responseType: 'blob' })
    if (res) {
      const blob = new Blob([res.data || res], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })

      // 优先 RFC5987 的 filename*=UTF-8''xxx；兼容历史 filename*=编码 无 charset 前缀
      let filename = '导出.xlsx'
      if (res.headers && res.headers['content-disposition']) {
        const disposition = res.headers['content-disposition']
        const utf8Match = /filename\*=(?:UTF-8|utf-8)''([^;\n]+)/i.exec(disposition)
        const starMatch = /filename\*=([^;\n]*)/i.exec(disposition)
        const plainMatch = /(?:^|;)\s*filename=([^;\n]*)/i.exec(disposition)
        const raw = utf8Match?.[1] || (starMatch?.[1] && !starMatch[1].toLowerCase().startsWith('utf-8') ? starMatch[1] : '') || plainMatch?.[1]
        if (raw) {
          try {
            filename = decodeURIComponent(String(raw).replace(/['"]/g, '').trim())
          } catch {
            filename = String(raw).replace(/['"]/g, '').trim()
          }
        }
      }

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.style.display = 'none'

      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
    // 下载完成后回调
    if (typeof onFinish === 'function') {
      onFinish()
    }
  } catch (error) {
    // 出错也回调
    if (typeof onFinish === 'function') {
      onFinish(error)
    }
    throw error
  }
}

/**
 * 公共导出模板方法
 */
export const p_DowTem = async (apiRequest: Promise<any>, defaultFileName: string) => {
  try {
    const res = await apiRequest

    const blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url

    // 解析文件名
    let fileName = defaultFileName + '模板'
    const contentDisposition = res.headers?.['content-disposition']

    if (contentDisposition) {
      const utf8FilenameRegex = /filename\*=UTF-8''([\w%\-\.]+)/i
      const asciiFilenameRegex = /filename="?([^"]+)"?/i

      const utf8Matches = utf8FilenameRegex.exec(contentDisposition)
      const asciiMatches = asciiFilenameRegex.exec(contentDisposition)

      if (utf8Matches && utf8Matches[1]) {
        fileName = decodeURIComponent(utf8Matches[1])
      } else if (asciiMatches && asciiMatches[1]) {
        fileName = asciiMatches[1]
      }
    }

    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    return { success: true }
  } catch (error) {
    console.error('下载失败', error)
    return { success: false, message: '下载失败' }
  }
}

/**
 * 证件号码正则校验方法（优化版-直接提示错误信息）
 * @param fieldValue 需要校验的字段值
 * @param validateTypeCode 校验类型code：1(居民身份证)、3(护照)、6(军官证)、17(驾驶证)
 * @returns 校验结果对象，包含isValid(是否有效)和message(提示信息)
 */
export const p_ValidateIdDocument = (fieldValue: string, validateTypeCode: string) => {
  // 去除首尾空格
  const value = fieldValue?.trim() || ''

  // code到校验类型的映射
  const codeToTypeMap = {
    '1': 'idCard', // 居民身份证
    '3': 'passport', // 护照
    '6': 'military', // 军官证
    '17': 'driver' // 驾驶证
  }

  // 如果不是指定的4种证件类型，直接返回通过
  if (!Object.prototype.hasOwnProperty.call(codeToTypeMap, validateTypeCode)) {
    return {
      isValid: true,
      message: ''
    }
  }

  // 映射到具体的校验类型
  const validateType = codeToTypeMap[validateTypeCode]

  // 各类证件的正则表达式
  const patterns = {
    // 身份证：18位，最后一位可能是数字或X
    idCard: /^[1-9]\d{5}(18|19|20)\d{2}((0[1-9])|(1[0-2]))(([0-2][1-9])|10|20|30|31)\d{3}[0-9Xx]$/,
    // 护照：包含1位字母+8位数字 或 2位字母+7位数字
    passport: /^[A-Z]{1,2}\d{7,8}$/i,
    // 军官证：通常为汉字+数字组合，这里简化处理为至少6位的字母数字组合
    military: /^[\u4e00-\u9fa5]?[A-Za-z0-9]{6,20}$/,
    // 驾驶证：通常为18位数字，有时包含字母
    driver: /^[0-9]{12,18}$|^[0-9]{17}[0-9Xx]$/
  }

  // 错误提示信息
  const messages = {
    idCard: '身份证格式不正确，请输入18位有效身份证号',
    passport: '护照格式不正确，请输入1-2位字母加7-8位数字',
    military: '军官证格式不正确',
    driver: '驾驶证格式不正确，请输入12-18位数字'
  }

  // 空值校验
  if (!value) {
    $message.error('证件号码不能为空')
    return {
      isValid: false,
      message: '证件号码不能为空'
    }
  }

  // 正则校验
  const isValid = patterns[validateType].test(value)

  // 校验失败时直接提示错误信息
  if (!isValid) {
    $message.error(messages[validateType])
  }

  return {
    isValid,
    message: isValid ? '' : messages[validateType]
  }
}

/**
 * 处理UploadType组件的previewFileId值
 * @param schemaList 表单schema数组(数据源)
 * @param fieldName 需要处理的字段名称（可选）
 * @param fileId 要设置的文件ID值（可选，不传则清空）
 * @returns 处理后的schemaList
 */
export function p_FileId(schemaList, fieldName?: string | string[], fileId?: string | string[]) {
  // 创建新的数组避免直接修改原数组
  const newSchemaList = [...schemaList]

  // 只传入schemaList，清除所有UploadType组件的previewFileId
  if (arguments.length === 1) {
    newSchemaList.forEach((item) => {
      if (item.component === 'UploadType') {
        if (!item.componentProps) {
          item.componentProps = {}
        }
        item.componentProps.previewFileId = ''
      }
    })
    return newSchemaList
  }

  // 传入schemaList和fieldName，清除指定字段的previewFileId
  if (arguments.length === 2) {
    newSchemaList.forEach((item) => {
      // 支持字符串或数组类型的fieldName
      const shouldProcess = Array.isArray(fieldName)
        ? fieldName.includes(item.field)
        : item.field === fieldName

      if (shouldProcess && item.component === 'UploadType') {
        if (!item.componentProps) {
          item.componentProps = {}
        }
        item.componentProps.previewFileId = ''
      }
    })
    return newSchemaList
  }

  // 传入完整参数，设置指定字段的previewFileId值
  if (arguments.length === 3) {
    // 当fieldName和fileId都是数组时，按位置对应设置值
    if (Array.isArray(fieldName) && Array.isArray(fileId)) {
      newSchemaList.forEach((item) => {
        const fieldIndex = fieldName.indexOf(item.field)
        if (fieldIndex !== -1 && item.component === 'UploadType') {
          if (!item.componentProps) {
            item.componentProps = {}
          }
          item.componentProps.previewFileId = fileId[fieldIndex] || ''
        }
      })
    } else {
      // 原有逻辑：单个字段名对应单个文件ID
      newSchemaList.forEach((item) => {
        // 支持字符串或数组类型的fieldName
        const shouldProcess = Array.isArray(fieldName)
          ? fieldName.includes(item.field)
          : item.field === fieldName

        if (shouldProcess && item.component === 'UploadType') {
          if (!item.componentProps) {
            item.componentProps = {}
          }
          item.componentProps.previewFileId = fileId || ''
        }
      })
    }
    return newSchemaList
  }

  return newSchemaList
}

/**
 * 处理菜单按钮权限
 * @param btnText 要判断的按钮文本
 * @returns 是否存在该按钮
 */
export function p_MenuBtn(btnText?: string | string[]) {
  const btnListArr = getStorage('btnList') || []
  if (!Array.isArray(btnListArr) || btnListArr.length === 0) {
    return false
  }
  // 获取当前页面的菜单id
  const currentRoute = useRouter()?.currentRoute?.value
  const menuId = (currentRoute?.meta?.id as string) || ''
  const menuCode = (currentRoute?.name as string) || ''

  // 优先 menuId 过滤；若环境里 menuId 不一致，再用 menuCode 兜底
  let menuBtnList = btnListArr.filter((item) => item.parentId === menuId)
  if (!menuBtnList.length && menuCode) {
    menuBtnList = btnListArr.filter(
      (item) => item.parentCode === menuCode || item.menuCode === menuCode
    )
  }
  if (!menuBtnList.length) {
    menuBtnList = btnListArr
  }

  const hasOne = (text: string) =>
    menuBtnList.some((item) => item.name === text || item.code === text)
  // 判断当前传入的按钮文本是否在筛选的菜单按钮中, 返回布尔值，如果传入的是list，则判断这几个是否都存在，都存在则返回true
  return Array.isArray(btnText)
    ? btnText.every((text) => hasOne(text))
    : hasOne(btnText as string)
}

// 文件列表缓存，以避免重复请求
const fileListCache = new Map<string, any[]>()

/**
 * 根据文件bizId获取对应的附件图片列表
 */
export const p_GetFileListByBizId = async (
  bizId: string,
  bizKind: string | undefined,
  subKind?: string
) => {
  // 检查缓存中是否已有该bizId的数据
  let fileList = fileListCache.get(bizId)

  if (!fileList) {
    // 如果缓存中没有，则发起请求获取数据
    const res = await getFileListApi(bizId, bizKind)
    console.log('图片列表数据', res)
    if (res.code === 0) {
      fileList = res.data
      // 将数据存入缓存
      fileListCache.set(bizId, fileList)
    } else {
      return []
    }
  }

  // 如果传入了subKind参数，则筛选出相同subKind的图片
  if (subKind !== undefined && subKind !== null) {
    return fileList.filter((item) => item.subKind === subKind)
  }

  return fileList
}

// 清除指定bizId的缓存
export const p_ClearFileListCache = (bizId?: string) => {
  if (bizId) {
    fileListCache.delete(bizId)
  } else {
    // 清除所有缓存
    fileListCache.clear()
  }
}

/**
 * 根据bizId和subKind获取图片并预览
 */
export const p_GetAndPreviewFileBySubKind = async (
  bizId: string,
  bizKind: string,
  subKind?: string
) => {
  const filteredFiles = await p_GetFileListByBizId(bizId, bizKind)

  if (subKind) {
    // 如果传入了fileName，查找匹配的文件并预览
    const targetFile = filteredFiles.find(
      (file) => file.subKind === subKind || file.subKind === subKind
    )
    if (targetFile) {
      return await p_PreviewFile(targetFile.id)
    }
  } else if (filteredFiles.length > 0) {
    // 如果没有指定fileName，预览第一个文件
    return await p_PreviewFile(filteredFiles[0].id)
  }

  return null
}

// 公共方法处理菜单数据，构建树结构
export const p_BuildTree = (list, typeFilter?) => {
  // 如果传入了typeFilter，则先筛选对应类型的节点
  const filteredList = typeFilter ? list.filter((item) => item.type === typeFilter) : list

  // 创建一个映射，方便快速查找节点
  const map = {}
  const roots = []

  // 初始化所有节点，添加children属性
  filteredList.forEach((item) => {
    // 为按钮类型节点添加特殊标识，以便在UI中区分
    map[item.id] = {
      ...item,
      children: [],
      isButton: item.type === 'btn' // 添加按钮标识
    }
  })

  // 构建树结构（包括按钮）
  filteredList.forEach((item) => {
    const node = map[item.id]

    // 如果是按钮类型且没有找到父节点，则跳过此节点
    if (item.type === 'btn' && (!item.parentId || item.parentId === '' || !map[item.parentId])) {
      return // 不将此按钮节点添加到树中
    }

    if (!item.parentId || item.parentId === '' || !map[item.parentId]) {
      // 没有parentId或者parentId找不到对应节点，则为根节点（非按钮类型）
      roots.push(node)
    } else {
      // 有parentId且能找到对应节点，则添加到父节点的children中
      const parent = map[item.parentId]
      parent.children.push(node)
    }
  })

  // 对根节点按sort字段排序
  roots.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))

  // 递归对所有子节点按sort字段排序
  function sortChildren(nodes) {
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        node.children.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
        sortChildren(node.children)
      }
    })
  }

  sortChildren(roots)

  return roots
}

// 筛选对应城市名称
export const p_FindCityName = (values, options) => {
  if (!values || !Array.isArray(values) || values.length === 0) return []

  const labels = []
  let currentOptions = options

  for (const value of values) {
    const foundOption = currentOptions.find((option) => option.value === value)
    if (foundOption) {
      labels.push(foundOption.label)
      currentOptions = foundOption.children || []
    } else {
      break
    }
  }

  return labels
}

/**
 * 通用表格合计方法
 * @param summaryConfig - 合计配置对象，格式：{ fieldName: precision }
 * @example { feeAmount: 2, quantity: 0 } 表示 feeAmount 保留2位小数，quantity 不保留小数
 */
export const p_TotalTableAmount = (summaryConfig = {}) => {
  return (param) => {
    const { columns, data } = param
    const sums = []

    columns.forEach((column, index) => {
      if (index === 0) {
        sums[index] = '合计'
      } else if (Object.hasOwn(summaryConfig, column.property)) {
        const values = data.map((item) => Number(item[column.property] || 0))
        const sum = values.reduce((prev, curr) => prev + curr, 0)

        // 根据配置决定保留几位小数
        const precision = summaryConfig[column.property]
        sums[index] = precision > 0 ? sum.toFixed(precision) : sum
      } else {
        sums[index] = ''
      }
    })

    return sums
  }
}
